import { Vendor } from 'api/interfaces';
import { getCookie } from './cookie';
import logger from 'util/logger';

const CONSENTED_VENDORS = 'consentedvendors-v2';
const EUCONSENT = 'euconsent-v2';
const CCONSENT = 'cconsent-v2';
const talpaDomains = [
  '538.nl',
  'hartvannederland.nl',
  'juke.nl',
  'kijk.nl',
  'kijkers.tv ',
  'linda.nl',
  'new-wave.nl ',
  'partnerplayer.talparadio.nl',
  'radio10.nl',
  'radioveronica.nl ',
  'sbs.nl',
  'show.nl',
  'shownieuws.nl',
  'skyradio.nl',
  'social1nfluencers.nl',
  'stuk.tv',
  'talpanetwork.com',
  'veronicainside.nl',
  'weer.nl',
];

/**
 * getEUConsentString
 * @return {string}
 */
export function getEUConsentString(): string {
  return getCookie(EUCONSENT);
}

/**
 * getCConsentString
 * @return {string}
 */
export function getCConsentString(): string {
  return getCookie(CCONSENT);
}

export const getUrlWithConsent = (source: string): string => {
  // check if source is a talpa domain
  if (!talpaDomains.some((talpaDomain) => source.includes(talpaDomain))) {
    return source;
  }

  const euconsent = getEUConsentString();
  const cconsent = getCConsentString();

  let queryString = '';
  if (euconsent && cconsent) {
    queryString = `euconsent-v2=${euconsent}&cconsent-v2=${cconsent}`;
  } else if (euconsent) {
    queryString = `euconsent-v2=${euconsent}`;
  } else if (cconsent) {
    queryString = `cconsent-v2=${cconsent}`;
  } else {
    return source;
  }

  if (source.indexOf('?') === -1) {
    return `${source}?${queryString}`;
  }
  return `${source}&${queryString}`;
};

/**
 * Check if vendor has consent
 *
 * @param {Vendor} vendor
 */
export default function vendorAllowed(vendor: Vendor): boolean {
  if (window.isVendorAllowed(vendor) === false) {
    logger.warn(`%c Vendor ${vendor.name} has NOT been given consent`, 'background-color:darkred; color: white');
    return false;
  }
  return true;
}

/**
 * Check a list of vendors for consent
 * @param {array} vendorArray
 * @param {string} source
 */
export const checkVendors = (vendorArray: [Vendor], source: string): boolean => {
  if (!vendorArray.some(({ url }) => source.includes(url))) {
    logger.warn(`source ${source} not in vendorlist`);
    return true;
  }
  return vendorArray.some((vendor) => source.includes(vendor.url) && vendorAllowed(vendor));
};

/**
 * Checks if a given array of purposes is allowed. Checks purposes against the
 * consentedVendors cookie and uses actual EU consent cookie as backup source.
 * @param {array} purposes
 * @returns {Boolean}
 */
function getPurposesAllowed(purposes: [number]): boolean {
  if (Array.isArray(purposes) === false) {
    logger.error('TypeError: Function "util/arePurposesAllowed()" expected an array of purposes to check against.');
    return false;
  }

  const consentedVendors = JSON.parse(getCookie(CONSENTED_VENDORS));
  if (!consentedVendors) return false;
  const consentedPurposes = consentedVendors.consented_purposes_v2 || [];
  return purposes.every((purpose) => consentedPurposes.includes(purpose));
}

/**
 * Returns the consent query parameter for Triton stream and caches this. We expect
 * the value to only change via external privacy site so a refresh is inevitable.
 * @returns {string}
 */
export const tritonConsentTag: string = (() => {
  const { purposes } = window.VENDORS.TALPA_CONSENT;

  if (getPurposesAllowed(purposes)) {
    return 'ttag=talpa_consent:1';
  }

  return 'ttag=talpa_consent:0';
})();
